@let isMobile = isMobile$ | async;
@let toggleSideDrawer = toggleSideDrawer$ | async;
@let isNonInteractive = isEngagementNonInteractive$ | async;
@let selectedEngagement = selectedEngagement$ | async;
@let engagementReportStatus = engagementReportStatus$ | async;
@let isProcessingSubmission = isProcessingSubmission$ | async;
@let controlsDisabled = controlsDisabled$ | async;

<mat-drawer-container>
  <!-- Drawer -->
  @if (!isUsingSecureLink()) {
    <mat-drawer
      [opened]="showSideDrawer$ | async"
      [mode]="sideDrawerMode$ | async"
      (openedChange)="drawerStateChanged($event)"
      class="view-engagement-sidebar"
    >
      @if (selectedEngagement) {
        <div class="view-engagement-sidebar-info fw--300">
          <aiwyn-logo [showInternalLogo]="true" [extraClasses]="'view-engagement-sidebar-info-logo mb--125'" [alt]="configService.customerName + ' Logo'"></aiwyn-logo>

          @let isMultipleIdentitySigningActive = isMultipleIdentitySigningActive$ | async;

          @if (isMultipleIdentitySigningActive && isContactSelectDropdownVisible) {
            @let contactSelectOptions = contactSelectOptions$ | async;
            @let activeSigningContactId = activeSigningContactId$ | async;

            <app-engagement-contact-select
              [options]="contactSelectOptions"
              [value]="activeSigningContactId"
              (selectionChange)="onSigningContactChanged($event)"
            ></app-engagement-contact-select>
          }

          <p class="fw--500">{{ selectedEngagement.clientName }}</p>
          <p>{{ selectedEngagement.name }}</p>

          <p class="tc--dark mt--125">Partner:</p>
          <p>{{ selectedEngagement.partnerName }}</p>

          <p class="tc--dark mt--125">Start date:</p>
          <p>{{ selectedEngagement.startDate }}</p>

          @if (selectedEngagement.status === EngagementStatus.UNDER_REVISION) {
            <div class="alert mt--125">
              This engagement is currently under revision. When a new version is ready for review, you will be notified.
            </div>
          }

          @if (selectedEngagement.status === EngagementStatus.CANCELED) {
            <div class="alert alert--error mt--125">
              This engagement has been canceled by {{ configService.customerName }}. Please reach out to the firm if you have any questions.
            </div>
          }

          @if (!isNonInteractive) {
            @if (selectedEngagement.mode === EngagementMode.MANUAL && selectedEngagement.status === EngagementStatus.SENT) {
              <p class="mt--125">
                {{ selectedEngagement.clientName }} has sent you a physical copy of this engagement.
              </p>
            }

            @if (selectedEngagement.executedDate) {
              <p class="tc--dark mt--125">Executed At:</p>
              <p>{{ selectedEngagement.executedDate }}</p>
            }

            @if (selectedEngagement.mode === EngagementMode.MANUAL && selectedEngagement.status === EngagementStatus.EXECUTED) {
              <p class="mt--125">
                {{ selectedEngagement.clientName }} has executed this engagement{{ (isEngagementLetterManuallyUploaded$ | async) ? ' and uploaded a physical copy of the signed letter' : '' }}.
              </p>
            }

            @if (!(isEngagementReadonly$ | async)) {
              <p class="mt--125 fw--500">
                {{ configService.customerName }} has sent you this engagement for you to review and complete.
              </p>
            }

            @if (someSelectedEngagementStepsInvalid$ | async) {
              <div class="alert alert--error mt--125">
                Your progress has been saved, but there are still actions required. You can come back later to complete them if necessary.
              </div>
            }
          }
        </div>
      }

      @if (!isNonInteractive && selectedEngagement?.mode === EngagementMode.AUTOMATIC && engagementReportStatus) {
        <div class="view-engagement-sidebar-steps">
          <ng-container [ngSwitch]="engagementReportStatus">
            <ng-container *ngSwitchCase="LoadableStatus.LOADED">
              @let currentStep = currentStep$ | async;
              @if (currentStep) {
                @for (step of visibleEngagementSteps$ | async; track step) {
                  <div
                    class="view-engagement-sidebar-steps-item"
                    (click)="onGoToStep(stepReference(step))"
                    [ngClass]="{
                    'view-engagement-sidebar-steps-item-current fw--500': isCurrentStep(step),
                    'view-engagement-sidebar-steps-item-disabled': controlsDisabled
                  }">
                    <p class="truncate me-2">{{ step.label }}</p>
                    @if (step.hasTasksForCurrentUser) {
                      <ng-container [ngSwitch]="step.status">
                        <aiwyn-icon *ngSwitchCase="EngagementStepStatus.INCOMPLETE" name="circle" height="15" width="15"></aiwyn-icon>

                        <aiwyn-icon *ngSwitchCase="EngagementStepStatus.COMPLETE"
                          class="engagement-step-complete"
                          name="check-circle"
                          height="15"
                          width="15">
                        </aiwyn-icon>

                        <pill *ngSwitchCase="EngagementStepStatus.INVALID" color="red" [dark]="true" size="small" value="ACTION REQUIRED"></pill>

                        <pill
                          *ngSwitchCase="EngagementStepStatus.FAILED"
                          color="red"
                          [dark]="true"
                          size="small"
                          [value]="ENGAGEMENT_STEP_FAILED_MESSAGE[step.type]"
                        ></pill>
                      </ng-container>
                    }
                  </div>
                }
              }
            </ng-container>

            <div *ngSwitchCase="LoadableStatus.ERROR" class="alert alert--error">
              Could not retrieve engagement details. Please try again later.
            </div>

            <div *ngSwitchDefault class="d-flex justify-content-center align-items-center my-12">
              <mat-spinner diameter="48"></mat-spinner>
            </div>
          </ng-container>
        </div>
      }

      <div class="view-engagement-sidebar-exit-container d-flex flex-column align-items-start justify-content-end gap-4">

        @let isSelectedEngagementReassignable = isSelectedEngagementReassignable$ | async;
        @if (!isNonInteractive && configService.engagementReassignmentEnabled && isSelectedEngagementReassignable) {
          <button class="view-engagement-sidebar-exit p-2" (click)="navigateToReassignment(selectedEngagement.id)">
            <aiwyn-icon name="send" [height]="16" [width]="16"></aiwyn-icon>
            Reassign engagement…
          </button>
        }

        <button (click)="navigateToEngagementsTable()" class="view-engagement-sidebar-exit">
          <aiwyn-icon name="arrow-left" [height]="16" [width]="16"></aiwyn-icon>
          Cancel and go back
        </button>
      </div>
    </mat-drawer>
  }

  <!-- Content -->
  <mat-drawer-content>
    @if (!isNonInteractive) {
      <ng-container [ngSwitch]="engagementReportStatus">
        <div *ngSwitchCase="LoadableStatus.LOADED" class="view-engagement-content">
          <!-- Mobile Header -->
          <div class="engagement-detail-mobile-header">
            <div class="engagement-detail-title">
              <button (click)="toggleSideDrawer$.next(!toggleSideDrawer)">
                <aiwyn-icon name="more-vertical" height="20" width="20"></aiwyn-icon>
              </button>
              <div class="h4">
                <!-- TODO: Handle mobile view for engagement step label -->
                {{ (currentStep$ | async)?.label || EngagementStepLabel[((currentStep$ | async)?.type)] }}
              </div>
            </div>

            <div>
              {{ currentStepIndex$ | async }}/{{ totalSteps$ | async }}
            </div>
          </div>

          @let currentStepTemplate = currentStepTemplateSubject$ | async;
          @if (showStepComponent && currentStepTemplate) {
            <ng-container [ngTemplateOutlet]="currentStepTemplate.ref" [ngTemplateOutletContext]="{ $implicit: currentStepTemplate.context }">
            </ng-container>
          }

          @let footerButtonTemplateRefs = footerButtonTemplateRefs$ | async;
          @if (footerButtonTemplateRefs) {
            @if (footerButtonTemplateRefs.length > 0) {
              <div
                class="view-engagement-content-actions"
                [ngClass]="{'view-engagement-content-actions_mobile': isMobile}"
              >
                @for (footerButtonTemplateRef of footerButtonTemplateRefs; track footerButtonTemplateRef) {
                  <div
                    class="view-engagement-content-actions-item"
                    [ngClass]="{ 'view-engagement-content-actions-item-disabled': controlsDisabled }">
                    <ng-container [ngTemplateOutlet]="footerButtonTemplateRef"></ng-container>
                  </div>
                }
              </div>
            }
          }
        </div>

        <div *ngSwitchCase="LoadableStatus.ERROR" class="view-engagement-content-error">
          <div class="alert alert--error">Could not retrieve engagement details. Please try again later.</div>
        </div>

        <div *ngSwitchDefault class="view-engagement-content-loading">
          <mat-spinner [diameter]="100"></mat-spinner>
        </div>
      </ng-container>
    }
  </mat-drawer-content>
</mat-drawer-container>

<ng-template #LETTER let-engagementStep>
  <app-engagement-letter-step #engagementStepComponent [details]="engagementStep"></app-engagement-letter-step>
</ng-template>

<ng-template #CONSENT let-engagementStep>
  <app-consent-step #engagementStepComponent [details]="engagementStep"></app-consent-step>
</ng-template>

<ng-template #PAYMENT let-engagementStep>
  <app-payment-info-step #engagementStepComponent [details]="engagementStep" [submitButtonLabel]="completeButtonText$ | async"></app-payment-info-step>
</ng-template>

<ng-template #previousField>
  <button (click)="navigateToField('previous')" type="button" [disabled]="controlsDisabled" aria-labelledby="engagement-footer-previous-field-label">
    <aiwyn-icon name="arrow-up-circle" height="40" width="40"></aiwyn-icon>
  </button>
  <p id="engagement-footer-previous-field-label">Previous Field</p>

  <!-- Mobile view does not have a previous button -->
</ng-template>

<ng-template #nextField>
  <button (click)="navigateToField('next')" type="button" [disabled]="controlsDisabled" aria-labelledby="engagement-footer-next-field-label">
    <aiwyn-icon name="arrow-down-circle" height="40" width="40"></aiwyn-icon>
  </button>
  <p id="engagement-footer-next-field-label">Next Field</p>

  <!-- Mobile button -->
  <button
    andButton
    color="secondary"
    size="large"
    type="button"
    class="engagement-detail-footer-mobile-button"
    [disabled]="controlsDisabled"
    (click)="navigateToField('next', true)"
  >
    Next Field
  </button>
</ng-template>

<ng-template #previousStep>
  <button (click)="onGoToStep('previous')" type="button" [disabled]="controlsDisabled" aria-labelledby="engagement-footer-previous-step-label">
    <aiwyn-icon name="arrow-left-circle" height="40" width="40"></aiwyn-icon>
  </button>
  <p id="engagement-footer-previous-step-label">Back</p>

  <!-- Mobile button -->
  <button
    andButton
    color="secondary"
    size="large"
    (click)="onGoToStep('previous')"
    [disabled]="controlsDisabled"
    class="engagement-detail-footer-mobile-button"
    type="button">
    Back
  </button>
</ng-template>

<ng-template #approveAndSubmit>
  <button (click)="onSubmitStep()" type="button" class="complete-engagement" [disabled]="controlsDisabled" aria-labelledby="engagement-footer-approve-and-submit-label">
    <aiwyn-icon [name]="(isEngagementOnFinalStep$ | async) ? 'check-circle-2' : 'arrow-right-circle'" height="40" width="40"></aiwyn-icon>
  </button>
  <p id="engagement-footer-approve-and-submit-label" class="complete-engagement-label">{{ isProcessingSubmission ? 'Saving...' : 'Approve & Submit' }}</p>

  <!-- Mobile button -->
  <button
    andButton
    color="primary"
    size="large"
    (click)="onSubmitStep(true)"
    [disabled]="controlsDisabled"
    class="engagement-detail-footer-mobile-button final-button"
    type="button"
  >
    @if (!isProcessingSubmission) {
      {{ (isEngagementOnFinalStep$ | async) ? 'Complete' : 'Next' }}
      <aiwyn-icon [name]="(isEngagementOnFinalStep$ | async) ? 'check' : 'arrow-right'" height="16" width="16"></aiwyn-icon>
    } @else {
      Saving...
    }
  </button>
</ng-template>

<ng-template #nextStep>
  <!-- TODO: This may change if we decide not to force step submission before navigating away. -->
  <button (click)="onSubmitStep()" type="button" class="complete-engagement" [disabled]="controlsDisabled" aria-labelledby="engagement-footer-next-step-label">
    <aiwyn-icon name="arrow-right-circle" height="40" width="40"></aiwyn-icon>
  </button>
  <p id="engagement-footer-next-step-label" class="complete-engagement-label">{{ isProcessingSubmission ? 'Saving...' : 'Next' }}</p>

  <!-- Mobile button -->
  <button (click)="onSubmitStep()"
    andButton
    color="primary"
    size="large"
    [disabled]="controlsDisabled"
    class="engagement-detail-footer-mobile-button final-button"
    type="button">
    @if (!isProcessingSubmission) {
      Next
      <div><aiwyn-icon name="arrow-right" height="16" width="16"></aiwyn-icon></div>
    } @else {
      Saving...
    }
  </button>
</ng-template>

<ng-template #completeEngagement>
  <button (click)="onSubmitStep()" type="button" class="complete-engagement" [disabled]="controlsDisabled" aria-labelledby="engagement-footer-complete-engagement-label">
    <aiwyn-icon name="check-circle-2" height="40" width="40"></aiwyn-icon>
  </button>
  <p id="engagement-footer-complete-engagement-label" class="complete-engagement-label">{{ isProcessingSubmission ? 'Saving...' : completeButtonText$ | async }}</p>

  <!-- Mobile button -->
  <button
    andButton
    color="primary"
    size="large"
    (click)="onSubmitStep()"
    [disabled]="controlsDisabled"
    class="engagement-detail-footer-mobile-button final-button"
    type="button">
    @if (!isProcessingSubmission) {
      {{ completeButtonText$ | async }}
      <aiwyn-icon name="check" height="16" width="16"></aiwyn-icon>
    } @else {
      Saving...
    }
  </button>
</ng-template>

<!-- PP-3635: shown only while awaiting signatures (payment already confirmed) so the client can swap the payment method in place. -->
<ng-template #updatePaymentMethod>
  <button (click)="onSubmitStep(false, true)" type="button" class="complete-engagement" [disabled]="controlsDisabled" aria-labelledby="engagement-footer-update-payment-method-label">
    <aiwyn-icon name="check-circle-2" height="40" width="40"></aiwyn-icon>
  </button>
  <p id="engagement-footer-update-payment-method-label" class="complete-engagement-label">{{ isProcessingSubmission ? 'Saving...' : 'Update Payment Method' }}</p>

  <!-- Mobile button -->
  <button
    andButton
    color="primary"
    size="large"
    (click)="onSubmitStep(true, true)"
    [disabled]="controlsDisabled"
    class="engagement-detail-footer-mobile-button final-button"
    type="button"
  >
    @if (!isProcessingSubmission) {
      Update Payment Method
      <aiwyn-icon name="check" height="16" width="16"></aiwyn-icon>
    } @else {
      Saving...
    }
  </button>
</ng-template>

<ng-template #closeEngagement>
  <button (click)="navigateToEngagementsTable()" type="button" class="close-engagement" [disabled]="controlsDisabled" aria-labelledby="engagement-footer-close-engagement-label">
    <aiwyn-icon name="x-circle" height="40" width="40"></aiwyn-icon>
  </button>
  <p id="engagement-footer-close-engagement-label" class="close-engagement-label">Close</p>

  <!-- Mobile button -->
  <button
    andButton
    color="primary"
    size="large"
    (click)="navigateToEngagementsTable()"
    [disabled]="controlsDisabled"
    class="engagement-detail-footer-mobile-button final-button"
    type="button">
    Close
    <aiwyn-icon name="x" height="16" width="16"></aiwyn-icon>
  </button>
</ng-template>
